/* eslint-disable @typescript-eslint/no-explicit-any */
import { DatePicker, DatePickerProps } from 'antd';
import moment, { Moment } from 'moment';
import { useEffect, useLayoutEffect, useState, forwardRef } from 'react';
import { v4 as randomUUID } from 'uuid';

export type InputDateProps = DatePickerProps & { showTime?: boolean };
type InputElement = HTMLInputElement | null;

const numberRule = /\d+/g;
const formatDate = 'DD/MM/YYYY';
const formatDateTime = 'DD/MM/YYYY HH:mm';
const maxLenghtDate = 10;
const maxLenghtDateTime = 16;

const InputDate = forwardRef<typeof DatePicker, InputDateProps>(
  ({ placeholder = '', ...props }, ref) => {
    const newId = `input-date-${randomUUID()}`;
    const [input, setInput] = useState<HTMLInputElement | null>();
    const [value, setValue] = useState<Moment | null>();

    useEffect(() => {
      setValue(props.value);
    }, [props.value]);

    useLayoutEffect(() => {
      const inputElement = document.getElementById(newId) as InputElement;

      if (inputElement) {
        inputElement.maxLength = props.showTime
          ? maxLenghtDateTime
          : maxLenghtDate;
        inputElement.oninput = onInput;
        setInput(inputElement);
        inputElement.addEventListener('keydown', handleKeydownInput);
        inputElement.addEventListener('keyup', handleKeyupInput);
      }

      return () => {
        if (inputElement) {
          inputElement.removeEventListener('keydown', handleKeydownInput);
          inputElement.removeEventListener('keyup', handleKeyupInput);
        }
      };
    }, [input, props?.showTime]);

    function setInputValue(value: string) {
      if (input) {
        input.value = value;
        input.title = value;
      }
    }

    function onInput(this: GlobalEventHandlers) {
      const thisInput = this as HTMLInputElement;

      if (thisInput.value.length <= 10) {
        thisInput.value = thisInput.value.match(numberRule)?.join('/') ?? '';
      } else {
        const date =
          thisInput.value.slice(0, 10).match(numberRule)?.join('/') ?? '';
        let time = '';

        time = ` ${
          thisInput.value.slice(10).match(numberRule)?.join(':') ?? ''
        }`;

        if (
          thisInput.value.length === 11 &&
          thisInput.value.slice(-1) === ' '
        ) {
          time = '';
        }

        thisInput.value = `${date}${time}`;
      }
    }

    function handleKeydownInput() {
      if (!input) return null;

      let nextValue = input.value;

      if (nextValue.length === 3) {
        nextValue = `${nextValue.slice(0, 2)}/${nextValue.slice(2)}`;
      } else if (nextValue.length === 6) {
        nextValue = `${nextValue.slice(0, 5)}/${nextValue.slice(5)}`;
      } else if (props.showTime && nextValue.length === 11) {
        nextValue = `${nextValue.slice(0, 10)} ${nextValue.slice(10)}`;
      } else if (props.showTime && nextValue.length === 14) {
        nextValue = `${nextValue.slice(0, 13)}:${nextValue.slice(13)}`;
      }

      setInputValue(nextValue);

      return null;
    }

    function handleKeyupInput() {
      if (!input) return null;

      if (
        (!props.showTime && input.value.length === 10) ||
        (props.showTime && input.value.length === 16)
      ) {
        const date = moment(
          input.value,
          props.showTime ? formatDateTime : formatDate,
          true,
        );

        if (date.isValid()) {
          handleChange(date, input.value);
        }
      }

      return null;
    }

    function handleChange(date: Moment | null, dateString: string) {
      setValue(date);
      if (props.onChange) {
        props.onChange(date, dateString);
      }
    }

    return (
      <DatePicker
        {...props}
        ref={ref as any}
        placeholder={placeholder}
        value={value}
        id={newId}
        format={props.showTime ? formatDateTime : formatDate}
        onChange={handleChange}
        style={{ width: '100%', ...props.style }}
      />
    );
  },
);

export default InputDate;
